import React from 'react';

const inputBase: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 10,
  fontSize: 13,
  outline: 'none',
  transition: 'border-color 0.15s',
  backgroundColor: 'var(--bg-input)',
  border: '1px solid var(--bd)',
  color: 'var(--tx-1)',
  fontFamily: 'inherit',
};

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  suffix?: React.ReactNode;
}

export function Input({ label, error, hint, suffix, style, ...props }: InputProps) {
  const [focused, setFocused] = React.useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          style={{
            ...inputBase,
            borderColor: error ? 'rgba(239,68,68,0.4)' : focused ? 'rgba(16,185,129,0.5)' : undefined,
            paddingRight: suffix ? 40 : 12,
            ...style,
          }}
          onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
          onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
          {...props}
        />
        {suffix && (
          <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx-3)', fontSize: 12 }}>
            {suffix}
          </span>
        )}
      </div>
      {hint && !error && <span style={{ fontSize: 11, color: 'var(--tx-4)' }}>{hint}</span>}
      {error && <span style={{ fontSize: 11, color: '#f87171' }}>{error}</span>}
    </div>
  );
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: { value: string; label: string }[];
}

export function Select({ label, options, style, ...props }: SelectProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
          {label}
        </label>
      )}
      <select
        style={{ ...inputBase, cursor: 'pointer', ...style }}
        {...props}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ backgroundColor: 'var(--bg-input)' }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
}

export function Textarea({ label, style, ...props }: TextareaProps) {
  const [focused, setFocused] = React.useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-4)', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
          {label}
        </label>
      )}
      <textarea
        rows={3}
        style={{
          ...inputBase,
          resize: 'none',
          lineHeight: 1.6,
          borderColor: focused ? 'rgba(16,185,129,0.5)' : undefined,
          ...style,
        }}
        onFocus={(e) => { setFocused(true); props.onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); props.onBlur?.(e); }}
        {...props}
      />
    </div>
  );
}
